import { BUNDLED_EXPLORE_TEMPLATES } from '../data/explore_templates'
import type { TemplateRegistryEntry } from '../types/templates'

export interface MatchTemplatesInput {
  useCase?: string | null
  focusAreas?: string[] | null
  intentText?: string | null
  modules?: Partial<Record<string, boolean>> | null
}

const EXCLUDED_TAGS = new Set(['xp', 'spin', 'reward', 'celebrate'])

const USE_CASE_TAGS: Record<string, string[]> = {
  PERSONAL_GOALS: ['focus', 'weekly', 'reminder', 'hygiene'],
  TEAM_PROJECTS: ['task', 'weekly', 'planning', 'retro', 'agile'],
  STUDY_PLANNING: ['research', 'focus', 'weekly', 'planning'],
  CONTENT_PLANNING: ['content', 'video', 'podcast', 'publish', 'storyboard', 'campaign', 'marketing'],
  STARTUP_BUILDING: ['product', 'launch', 'strategy', 'sales', 'release'],
  FIELD_TEAM: ['ops', 'proof', 'safety', 'checklist', 'compliance'],
  SHOP_STOCK: ['ops', 'vendor', 'audit', 'checklist'],
  EVENTS: ['event', 'conference', 'vendor'],
  // AGENCY: tags from planner list verified to exist in explore_templates (A3)
  AGENCY: ['client', 'report', 'kickoff', 'campaign', 'content'],
}

const FOCUS_AREA_TAGS: Record<string, string[]> = {
  RESEARCH: ['research'],
  LEARNING: ['research', 'focus'],
  CREATIVE: ['content', 'storyboard', 'video'],
  CAREER: ['hr', 'review'],
  PRODUCTIVITY: ['focus', 'weekly', 'task'],
  HEALTH: ['reminder', 'hygiene'],
  SIDE_PROJECTS: ['product', 'launch'],
}

/**
 * Matches explore templates against onboarding answers (Plan §5 + OC8 + OC22).
 * Shows 3–6 template IDs, drops templates with gamification tags (xp, spin, reward, celebrate),
 * drops templates whose module is disabled when modules are given, and breaks ties by file order.
 */
export function matchTemplates(input: MatchTemplatesInput): string[] {
  const targetTags = new Set<string>()

  if (input.useCase && USE_CASE_TAGS[input.useCase]) {
    for (const tag of USE_CASE_TAGS[input.useCase]) {
      targetTags.add(tag.toLowerCase())
    }
  }

  if (Array.isArray(input.focusAreas)) {
    for (const focus of input.focusAreas) {
      const tags = FOCUS_AREA_TAGS[focus]
      if (tags) {
        for (const tag of tags) {
          targetTags.add(tag.toLowerCase())
        }
      }
    }
  }

  if (input.intentText && typeof input.intentText === 'string') {
    const words = input.intentText.toLowerCase().match(/\b[a-z0-9_-]+\b/g)
    if (words) {
      for (const word of words) {
        // Only consider words that appear as known explore template tags
        for (const tagsList of Object.values(USE_CASE_TAGS)) {
          if (tagsList.includes(word)) targetTags.add(word)
        }
        for (const tagsList of Object.values(FOCUS_AREA_TAGS)) {
          if (tagsList.includes(word)) targetTags.add(word)
        }
      }
    }
  }

  // Filter eligible templates
  const candidates: Array<{ template: TemplateRegistryEntry; score: number; fileIndex: number }> = []

  BUNDLED_EXPLORE_TEMPLATES.forEach((template, index) => {
    const templateTags = (template.tags || []).map((t) => t.toLowerCase())

    // Drop anything tagged xp/spin/reward/celebrate
    const isExcluded = templateTags.some((t) => EXCLUDED_TAGS.has(t))
    if (isExcluded) return

    // Drop templates whose module is off when modules is given
    if (input.modules) {
      if (template.category === 'Forms' && input.modules.formsEnabled === false) return
      if (template.category === 'Automations' && input.modules.automateEnabled === false) return
      if (template.category === 'Designs' && input.modules.designEnabled === false) return
    }

    // Score by tag overlap
    let score = 0
    if (targetTags.size > 0) {
      for (const tag of templateTags) {
        if (targetTags.has(tag)) score++
      }
    }

    candidates.push({
      template,
      score,
      fileIndex: index,
    })
  })

  // Sort descending by score, tie-break by file order
  candidates.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score
    return a.fileIndex - b.fileIndex
  })

  // Shows 3–6:
  // If we have positive-score matches, take min(6, max(3, positiveMatchesCount)).
  // If none or fewer than 3 positive, take 3 in file order.
  const positiveCount = candidates.filter((c) => c.score > 0).length
  const takeCount = Math.min(candidates.length, Math.min(6, Math.max(3, positiveCount)))

  return candidates.slice(0, takeCount).map((c) => c.template.id)
}
